/**
 * Skeletal animation system for the Legionnaire web prototype.
 *
 * ARCHITECTURE (20,000+ unit target)
 * ---------------------------------
 * Per-soldier skeletons do not scale. This module is built around three
 * formation-level sharing rules:
 *
 * 1. **One animation state per formation.** Every soldier in a formation
 *    shares a single AnimController (idle/walk/attack + blend). Per-soldier
 *    variation comes only from a cheap phase offset, so 500 soldiers cost
 *    one state machine, not 500.
 * 2. **Baked bone atlas, zero per-frame CPU skinning.** All clips are baked
 *    once into a single float DataTexture (bones x frames x clips). The
 *    vertex shader (see render/skinned.ts) samples bone matrices from the
 *    atlas, so per-frame CPU work per animated entity is a handful of
 *    floats (clip indices + times), not matrix palettes.
 * 3. **Aggressive LOD.** Formations beyond `LOD_DISTANCE` render the old
 *    static capsule; only near formations pay vertex-shader skinning.
 *
 * SKELETON (placeholder, 16 bones)
 * --------------------------------
 * 0 hips (root)  1 spine  2 chest  3 head
 * 4 upperArmL  5 lowerArmL  6 handL (prop socket: saw)
 * 7 upperArmR  8 lowerArmR  9 handR (prop socket: hammer)
 * 10 upperLegL 11 lowerLegL 12 footL
 * 13 upperLegR 14 lowerLegR 15 footR
 *
 * The placeholder skeleton is deliberately small: 16 bones x 4 texels fit a
 * 64-texel-wide atlas row, and a 300-triangle humanoid reads fine at RTS
 * camera distance (the user's stated visual bar).
 *
 * REAL-CLIP SWAP POINT
 * --------------------
 * The art manifest names the faber animation set
 * (AN_faber_idle/walk/hammer_high/hammer_low/saw/dig/repair), and the
 * vendored unit GLBs now carry real UE attack clips (e.g.
 * A_principes_kit_Attack, A_faber_Attack — 7 kits as of 2026-09-22).
 * Clips whose names match a placeholder slot REPLACE that slot in the
 * atlas; clips with UE-style names are aliased by attackClipAlias()
 * (attack -> the unit's sustained melee-fight slot; the faber's strike ->
 * its overhead hammer construction slot). Indices stay stable so live
 * meshes need no rebuild.
 *
 * Retargeting: UE skeletons use mannequin bone names, so sampleGltfClip()
 * remaps tracks through GLTF_BONE_REMAP onto the 16-bone placeholder rig
 * (rotation-only; unmapped bones are dropped). The state machine, the
 * shader, and the LOD policy are all clip-source agnostic.
 *
 * PER-UNIT ANIMATION AVAILABILITY (vendored GLBs, 2026-09-22)
 * ----------------------------------------------------------
 * 7 unit kits vendor a real attack clip: auxilia_infantry, faber,
 * gaul_warrior, imperial (orphaned — no data unit resolves to
 * imperial_kit), principes, samnite_warrior, velites. Everything else
 * uses the procedural set until its export lands. The `animations` field
 * on ResolvedModel (artmap.ts) is the runtime source of truth for the
 * manifest-declared sets; clipSetFor() below prefers real names when
 * present.
 *
 * PROTOTYPE MAPPING (web -> UE): this module is the web transcription of
 * the UE animation blueprint/state machine. Sim states (WorkerState,
 * formation inMelee/moving) are the shared vocabulary; the UE side keeps
 * its own skeletons and retargets the same state names.
 */
import * as THREE from "three";
import type { ResolvedModel } from "../data/artmap";
import type { UnitDef } from "../data/types";
import type { ModelStore } from "./models";

/* ------------------------------------------------------------------ */
/* Skeleton                                                            */
/* ------------------------------------------------------------------ */

/** Bone names; GLB exports must use these node names for the auto-swap. */
export const BONE_NAMES = [
  "hips",
  "spine",
  "chest",
  "head",
  "upperArmL",
  "lowerArmL",
  "handL",
  "upperArmR",
  "lowerArmR",
  "handR",
  "upperLegL",
  "lowerLegL",
  "footL",
  "upperLegR",
  "lowerLegR",
  "footR",
] as const;

export const BONE_COUNT = BONE_NAMES.length;

/**
 * Unreal/MetaHuman bone name -> placeholder BONE_NAMES. The vendored unit
 * GLBs are UE exports: their skeletons use the UE mannequin naming
 * (pelvis, spine_01/02/03, upperarm_l, thigh_l, ...). sampleGltfClip()
 * remaps animation tracks through this table onto the 16-bone placeholder
 * rig; bones with no entry (clavicles, neck_01, spine_01, ball/toe bones,
 * prop meshes) are dropped. Rotation-only: position/scale tracks are
 * ignored so the placeholder's proportions stay stable.
 *
 * Spine choice: the attack clips key spine_03 (upper spine), so it drives
 * the placeholder chest; spine_01/02 carry no keys in the vendored clips.
 * The GLB head bone drives the placeholder head (neck_01's motion is
 * dropped — the placeholder has no neck joint).
 */
export const GLTF_BONE_REMAP: Record<string, string> = {
  pelvis: "hips",
  spine_03: "chest",
  head: "head",
  upperarm_l: "upperArmL",
  lowerarm_l: "lowerArmL",
  hand_l: "handL",
  upperarm_r: "upperArmR",
  lowerarm_r: "lowerArmR",
  hand_r: "handR",
  thigh_l: "upperLegL",
  calf_l: "lowerLegL",
  foot_l: "footL",
  thigh_r: "upperLegR",
  calf_r: "lowerLegR",
  foot_r: "footR",
};

/** Parent bone index, or -1 for the root. */
export const BONE_PARENTS: readonly number[] = [
  -1, 0, 1, 2, 2, 4, 5, 2, 7, 8, 0, 10, 11, 0, 13, 14,
];

/** Bind-pose offset of each bone relative to its parent (metres, y-up). */
export const BONE_OFFSETS: ReadonlyArray<readonly [number, number, number]> = [
  [0, 1.0, 0], // hips
  [0, 0.12, 0], // spine
  [0, 0.18, 0], // chest
  [0, 0.22, 0], // head
  [-0.22, 0.14, 0], // upperArmL
  [-0.02, -0.28, 0], // lowerArmL
  [0, -0.26, 0], // handL
  [0.22, 0.14, 0], // upperArmR
  [0.02, -0.28, 0], // lowerArmR
  [0, -0.26, 0], // handR
  [-0.1, -0.02, 0], // upperLegL
  [0, -0.45, 0], // lowerLegL
  [0, -0.45, 0.06], // footL
  [0.1, -0.02, 0], // upperLegR
  [0, -0.45, 0], // lowerLegR
  [0, -0.45, 0.06], // footR
];

export function boneIndex(name: string): number {
  return (BONE_NAMES as readonly string[]).indexOf(name);
}

/* ------------------------------------------------------------------ */
/* Clip data                                                           */
/* ------------------------------------------------------------------ */

/** One keyframe: euler rotation (radians, XYZ) + optional root offset. */
export interface AnimKey {
  t: number;
  rx: number;
  ry: number;
  rz: number;
  px?: number;
  py?: number;
  pz?: number;
}

export interface AnimTrack {
  bone: number;
  keys: AnimKey[];
}

export interface AnimClip {
  name: string;
  duration: number;
  loop: boolean;
  /** "procedural" = placeholder; "gltf" = converted from a real export. */
  source: "procedural" | "gltf";
  /**
   * Timed events as fractions of the clip (0..1), e.g. the pilum release
   * point. The controller fires each once per loop; the game subscribes
   * (volley spawn, sound hooks later).
   */
  events?: Array<{ at: number; name: string }>;
  tracks: AnimTrack[];
}

/** Exact manifest names for the faber set (art_manifest.json). */
export const FABER_CLIP_NAMES = [
  "AN_faber_idle",
  "AN_faber_walk",
  "AN_faber_hammer_high",
  "AN_faber_hammer_low",
  "AN_faber_saw",
  "AN_faber_dig",
  "AN_faber_repair",
] as const;

export const GENERIC_CLIP_NAMES = [
  "AN_generic_idle",
  "AN_generic_walk",
  // Military set: functional-English placeholder names (see
  // MILITARY_CLIP_SWAP). No military clips exist in the art pipeline.
  "AN_infantry_march",
  "AN_infantry_double_march",
  "AN_infantry_run",
  "AN_infantry_defensive_stance",
  "AN_infantry_throw",
  "AN_infantry_spear_fight",
  "AN_infantry_spear_block",
  "AN_infantry_draw_weapon",
  "AN_infantry_gladius_fight",
  "AN_infantry_shield_block",
] as const;

/** Canonical atlas slot order. Stable across rebuilds: real GLB clips
 *  replace the slot with the same name, so live indices never shift. */
export const CLIP_ORDER: readonly string[] = [
  ...FABER_CLIP_NAMES,
  ...GENERIC_CLIP_NAMES,
];

/* ------------------------------------------------------------------ */
/* Activities                                                          */
/* ------------------------------------------------------------------ */

/**
 * The animation vocabulary the sim speaks. Render maps sim state ->
 * AnimActivity (workerActivity / formationActivity below); the controller
 * turns activities into blended clip poses.
 */
/**
 * Activity vocabulary shared by every animated thing.
 *
 * Faber (worker) states use the manifest's exact clip names (AN_faber_*).
 * Military states use FUNCTIONAL ENGLISH names only — the vendored kits
 * carry real attack clips under UE-style names (A_*_Attack), aliased at
 * boot onto the fight slots (see attackClipAlias), so these activities are
 * clearly-marked procedural placeholders with a documented swap table
 * (see MILITARY_CLIP_SWAP below). Do NOT invent Latin names for them.
 */
export type AnimActivity =
  // Faber (canonical manifest names).
  | "idle"
  | "walk"
  | "hammer_high"
  | "hammer_low"
  | "saw"
  | "dig"
  | "repair"
  // Military movement gaits (pace-driven).
  | "march"
  | "double_march"
  | "run"
  // Military combat.
  | "defensive_stance" // shields-up posture for defensive formations
  | "throw" // pilum throw (has a timed release point)
  | "spear_fight"
  | "spear_block"
  | "draw_weapon" // weapon-switch transition (pilum -> sidearm)
  | "gladius_fight"
  | "shield_block";

/**
 * Clip-name swap table: when real military clips are exported from the
 * art pipeline, they replace the same-named placeholder slots via
 * registerGltfClips() — no code changes needed. Status is honest for a
 * fresh system: every military entry ships as a procedural placeholder.
 * At boot, loadRealClipsForUnits() may replace the spear_fight and
 * gladius_fight slots with real UE attack clips (see attackClipAlias);
 * the table below is not updated at runtime — sys.clipSources() reports
 * the live per-clip source.
 */
export const MILITARY_CLIP_SWAP: Array<{
  activity: AnimActivity;
  clip: string;
  status: "placeholder" | "real";
}> = [
  { activity: "march", clip: "AN_infantry_march", status: "placeholder" },
  {
    activity: "double_march",
    clip: "AN_infantry_double_march",
    status: "placeholder",
  },
  { activity: "run", clip: "AN_infantry_run", status: "placeholder" },
  {
    activity: "defensive_stance",
    clip: "AN_infantry_defensive_stance",
    status: "placeholder",
  },
  { activity: "throw", clip: "AN_infantry_throw", status: "placeholder" },
  {
    activity: "spear_fight",
    clip: "AN_infantry_spear_fight",
    status: "placeholder",
  },
  {
    activity: "spear_block",
    clip: "AN_infantry_spear_block",
    status: "placeholder",
  },
  {
    activity: "draw_weapon",
    clip: "AN_infantry_draw_weapon",
    status: "placeholder",
  },
  {
    activity: "gladius_fight",
    clip: "AN_infantry_gladius_fight",
    status: "placeholder",
  },
  {
    activity: "shield_block",
    clip: "AN_infantry_shield_block",
    status: "placeholder",
  },
];

/**
 * Activity -> clip name, per clip set. Faber activities use the manifest's
 * exact AN_faber_* names; military activities use the functional-English
 * AN_infantry_* placeholder names from MILITARY_CLIP_SWAP.
 */
const ACTIVITY_CLIP: Record<AnimActivity, { faber: string; generic: string }> = {
  idle: { faber: "AN_faber_idle", generic: "AN_generic_idle" },
  walk: { faber: "AN_faber_walk", generic: "AN_generic_walk" },
  hammer_high: { faber: "AN_faber_hammer_high", generic: "AN_generic_idle" },
  hammer_low: { faber: "AN_faber_hammer_low", generic: "AN_generic_idle" },
  saw: { faber: "AN_faber_saw", generic: "AN_generic_idle" },
  dig: { faber: "AN_faber_dig", generic: "AN_generic_idle" },
  repair: { faber: "AN_faber_repair", generic: "AN_generic_idle" },
  march: { faber: "AN_faber_walk", generic: "AN_infantry_march" },
  double_march: { faber: "AN_faber_walk", generic: "AN_infantry_double_march" },
  run: { faber: "AN_faber_walk", generic: "AN_infantry_run" },
  defensive_stance: {
    faber: "AN_faber_idle",
    generic: "AN_infantry_defensive_stance",
  },
  throw: { faber: "AN_faber_hammer_high", generic: "AN_infantry_throw" },
  spear_fight: { faber: "AN_faber_hammer_low", generic: "AN_infantry_spear_fight" },
  spear_block: { faber: "AN_faber_idle", generic: "AN_infantry_spear_block" },
  draw_weapon: { faber: "AN_faber_idle", generic: "AN_infantry_draw_weapon" },
  gladius_fight: {
    faber: "AN_faber_hammer_low",
    generic: "AN_infantry_gladius_fight",
  },
  shield_block: { faber: "AN_faber_idle", generic: "AN_infantry_shield_block" },
};

/* ------------------------------------------------------------------ */
/* Procedural placeholder clips (CLEARLY MARKED — see header)          */
/* ------------------------------------------------------------------ */

function K(
  t: number,
  rx = 0,
  ry = 0,
  rz = 0,
  px?: number,
  py?: number,
  pz?: number,
): AnimKey {
  return { t, rx, ry, rz, px, py, pz };
}

function track(boneName: string, keys: AnimKey[]): AnimTrack {
  return { bone: boneIndex(boneName), keys };
}

const D2R = Math.PI / 180;

function idleClip(name: string, sway = 1): AnimClip {
  // NB: rotations are radians — earlier this passed raw degrees, which
  // bent idle figures ~114° at the chest.
  const s = sway * D2R;
  return {
    name,
    duration: 2.0,
    loop: true,
    source: "procedural",
    tracks: [
      track("chest", [K(0, 0, 0, 2 * s), K(1, 0, 0, -2 * s), K(2, 0, 0, 2 * s)]),
      track("head", [K(0, 0, 3 * s), K(1, 0, -3 * s), K(2, 0, 3 * s)]),
      track("upperArmL", [K(0, 0, 0, 4 * s), K(1, 0, 0, -4 * s), K(2, 0, 0, 4 * s)]),
      track("upperArmR", [K(0, 0, 0, -4 * s), K(1, 0, 0, 4 * s), K(2, 0, 0, -4 * s)]),
      track("hips", [K(0, 0, 0, 0, 0, 0.015 * sway), K(1, 0, 0, 0, 0, -0.015 * sway), K(2, 0, 0, 0, 0, 0.015 * sway)]),
    ],
  };
}

function walkClip(name: string): AnimClip {
  const swing = 32 * D2R;
  const arm = 22 * D2R;
  return {
    name,
    duration: 1.0,
    loop: true,
    source: "procedural",
    tracks: [
      track("upperLegL", [K(0, swing), K(0.5, -swing), K(1, swing)]),
      track("upperLegR", [K(0, -swing), K(0.5, swing), K(1, -swing)]),
      track("lowerLegL", [K(0, 0), K(0.25, 28 * D2R), K(0.5, 0), K(0.75, 10 * D2R), K(1, 0)]),
      track("lowerLegR", [K(0, 0), K(0.25, 10 * D2R), K(0.5, 0), K(0.75, 28 * D2R), K(1, 0)]),
      track("upperArmL", [K(0, -arm), K(0.5, arm), K(1, -arm)]),
      track("upperArmR", [K(0, arm), K(0.5, -arm), K(1, arm)]),
      track("hips", [
        K(0, 0, 0, 0, 0, 0.03),
        K(0.25, 0, 0, 0, 0, -0.03),
        K(0.5, 0, 0, 0, 0, 0.03),
        K(0.75, 0, 0, 0, 0, -0.03),
        K(1, 0, 0, 0, 0, 0.03),
      ]),
      track("chest", [K(0, 0, 4 * D2R), K(0.5, 0, -4 * D2R), K(1, 0, 4 * D2R)]),
    ],
  };
}

/** Right-arm hammer strike. `high` = full overhead swing, else short taps. */
function hammerClip(name: string, high: boolean): AnimClip {
  const up = high ? -150 * D2R : -70 * D2R;
  const down = high ? 25 * D2R : 15 * D2R;
  const bend = high ? 18 * D2R : 8 * D2R;
  return {
    name,
    duration: high ? 1.2 : 0.9,
    loop: true,
    source: "procedural",
    tracks: [
      track("upperArmR", [K(0, down), K(0.45, up), K(0.62, down), K(1, down)]),
      track("lowerArmR", [K(0, 10 * D2R), K(0.45, -25 * D2R), K(0.62, 5 * D2R), K(1, 10 * D2R)]),
      track("chest", [K(0, 0), K(0.45, -bend), K(0.62, bend), K(1, 0)]),
      track("hips", [K(0, 0, 0, 0, 0, 0, 0), K(0.62, 0, 0, 0, 0, -0.03, 0), K(1, 0, 0, 0, 0, 0, 0)]),
      track("upperArmL", [K(0, 8 * D2R, 0, 6 * D2R), K(1, 8 * D2R, 0, 6 * D2R)]),
    ],
  };
}

function sawClip(name: string): AnimClip {
  return {
    name,
    duration: 1.1,
    loop: true,
    source: "procedural",
    tracks: [
      // Right arm extended forward, hand pushes back and forth.
      track("upperArmR", [K(0, -55 * D2R), K(1, -55 * D2R)]),
      track("lowerArmR", [K(0, -10 * D2R), K(1, -10 * D2R)]),
      track("handR", [
        K(0, 0, 0, 0, 0, 0, 0),
        K(0.25, 0, 0, 0, 0, 0, -0.22),
        K(0.5, 0, 0, 0, 0, 0, 0),
        K(0.75, 0, 0, 0, 0, 0, -0.22),
        K(1, 0, 0, 0, 0, 0, 0),
      ]),
      track("chest", [K(0, 12 * D2R), K(0.5, 20 * D2R), K(1, 12 * D2R)]),
      track("hips", [K(0, 0, 0, 0, 0, -0.04, 0.05), K(1, 0, 0, 0, 0, -0.04, 0.05)]),
    ],
  };
}

function digClip(name: string): AnimClip {
  return {
    name,
    duration: 1.4,
    loop: true,
    source: "procedural",
    tracks: [
      track("chest", [K(0, 28 * D2R), K(0.5, 42 * D2R), K(1, 28 * D2R)]),
      track("upperArmL", [K(0, -40 * D2R), K(0.5, -70 * D2R), K(1, -40 * D2R)]),
      track("upperArmR", [K(0, -40 * D2R), K(0.5, -70 * D2R), K(1, -40 * D2R)]),
      track("hips", [
        K(0, 0, 0, 0, 0, -0.12, 0),
        K(0.5, 0, 0, 0, 0, -0.2, 0.08),
        K(1, 0, 0, 0, 0, -0.12, 0),
      ]),
      track("head", [K(0, -14 * D2R), K(1, -14 * D2R)]),
    ],
  };
}

function repairClip(name: string): AnimClip {
  // Light rapid taps with the hammer, slight crouch.
  return {
    name,
    duration: 0.8,
    loop: true,
    source: "procedural",
    tracks: [
      track("upperArmR", [K(0, -30 * D2R), K(0.25, -55 * D2R), K(0.5, -30 * D2R), K(0.75, -55 * D2R), K(1, -30 * D2R)]),
      track("lowerArmR", [K(0, 0), K(0.25, -12 * D2R), K(0.5, 0), K(0.75, -12 * D2R), K(1, 0)]),
      track("chest", [K(0, 16 * D2R), K(1, 16 * D2R)]),
      track("hips", [K(0, 0, 0, 0, 0, -0.1, 0), K(1, 0, 0, 0, 0, -0.1, 0)]),
    ],
  };
}

function attackClip(name: string): AnimClip {
  // Overhead weapon swing for melee combatants.
  return {
    name,
    duration: 1.1,
    loop: true,
    source: "procedural",
    tracks: [
      track("upperArmR", [K(0, -120 * D2R), K(0.4, -120 * D2R), K(0.6, 30 * D2R), K(1, -120 * D2R)]),
      track("lowerArmR", [K(0, -20 * D2R), K(0.6, 0), K(1, -20 * D2R)]),
      track("chest", [K(0, 0, -14 * D2R), K(0.4, 0, -14 * D2R), K(0.6, 0, 18 * D2R), K(1, 0, -14 * D2R)]),
      track("upperLegL", [K(0, 12 * D2R), K(0.6, 20 * D2R), K(1, 12 * D2R)]),
      track("hips", [K(0, 0, 0, 0, 0, 0, 0), K(0.6, 0, 0, 0, 0, -0.05, 0.1), K(1, 0, 0, 0, 0, 0, 0)]),
    ],
  };
}

/* ------------------------------------------------------------------ */
/* Military placeholders (CLEARLY MARKED — no military clips exist in   */
/* the art pipeline; see MILITARY_CLIP_SWAP for the replacement table) */
/* ------------------------------------------------------------------ */

/** Shared leg/arm swing for the three gait clips. */
function gaitTracks(swingDeg: number, armDeg: number, leanDeg: number): AnimTrack[] {
  const swing = swingDeg * D2R;
  const arm = armDeg * D2R;
  const lean = leanDeg * D2R;
  return [
    track("upperLegL", [K(0, swing), K(0.5, -swing), K(1, swing)]),
    track("upperLegR", [K(0, -swing), K(0.5, swing), K(1, -swing)]),
    track("lowerLegL", [K(0, 0), K(0.25, 30 * D2R), K(0.5, 0), K(0.75, 12 * D2R), K(1, 0)]),
    track("lowerLegR", [K(0, 0), K(0.25, 12 * D2R), K(0.5, 0), K(0.75, 30 * D2R), K(1, 0)]),
    track("upperArmL", [K(0, -arm), K(0.5, arm), K(1, -arm)]),
    track("upperArmR", [K(0, arm), K(0.5, -arm), K(1, arm)]),
    track("chest", [K(0, lean), K(0.5, lean), K(1, lean)]),
    track("hips", [
      K(0, 0, 0, 0, 0, 0.03),
      K(0.25, 0, 0, 0, 0, -0.03),
      K(0.5, 0, 0, 0, 0, 0.03),
      K(0.75, 0, 0, 0, 0, -0.03),
      K(1, 0, 0, 0, 0, 0.03),
    ]),
  ];
}

function marchClip(name: string): AnimClip {
  return {
    name, duration: 1.1, loop: true, source: "procedural",
    tracks: gaitTracks(28, 12, 3),
  };
}

function doubleMarchClip(name: string): AnimClip {
  return {
    name, duration: 0.75, loop: true, source: "procedural",
    tracks: gaitTracks(38, 24, 7),
  };
}

function runClip(name: string): AnimClip {
  return {
    name, duration: 0.55, loop: true, source: "procedural",
    tracks: gaitTracks(48, 40, 14),
  };
}

/** Shields-up crouch behind the shield line (testudo-style posture). */
function defensiveStanceClip(name: string): AnimClip {
  return {
    name, duration: 2.0, loop: true, source: "procedural",
    tracks: [
      // Crouch: hips down, thighs forward, knees bent.
      track("hips", [K(0, 0, 0, 0, 0, -0.14, 0), K(1, 0, 0, 0, 0, -0.12, 0)]),
      track("upperLegL", [K(0, 32 * D2R), K(1, 30 * D2R)]),
      track("upperLegR", [K(0, 32 * D2R), K(1, 30 * D2R)]),
      track("lowerLegL", [K(0, -48 * D2R), K(1, -46 * D2R)]),
      track("lowerLegR", [K(0, -48 * D2R), K(1, -46 * D2R)]),
      // Left arm raised with the shield covering the body.
      track("upperArmL", [K(0, -72 * D2R, 0, 18 * D2R), K(1, -70 * D2R, 0, 18 * D2R)]),
      track("lowerArmL", [K(0, -38 * D2R), K(1, -36 * D2R)]),
      // Right arm back, weapon ready.
      track("upperArmR", [K(0, 28 * D2R), K(1, 26 * D2R)]),
      track("chest", [K(0, 10 * D2R), K(0.5, 13 * D2R), K(1, 10 * D2R)]),
      track("head", [K(0, -8 * D2R), K(1, -8 * D2R)]),
    ],
  };
}

/**
 * Pilum throw. Non-looping; fires the "release" event at 45% of the clip
 * (the game spawns the volley tracer on that event — see render/volley).
 */
function throwClip(name: string): AnimClip {
  return {
    name, duration: 1.1, loop: false, source: "procedural",
    events: [{ at: 0.45, name: "release" }],
    tracks: [
      track("upperArmR", [
        K(0, 10 * D2R),
        K(0.3, 55 * D2R), // windup: arm back
        K(0.45, -85 * D2R), // release: arm forward
        K(0.62, -25 * D2R), // follow-through
        K(1, 0),
      ]),
      track("lowerArmR", [K(0, -30 * D2R), K(0.3, -50 * D2R), K(0.45, -8 * D2R), K(1, -20 * D2R)]),
      track("chest", [
        K(0, 0, 16 * D2R),
        K(0.3, -6 * D2R, 26 * D2R),
        K(0.45, 10 * D2R, -20 * D2R),
        K(1, 0, 0),
      ]),
      track("upperArmL", [K(0, 0), K(0.3, -30 * D2R), K(0.45, 20 * D2R), K(1, 0)]),
      track("upperLegL", [K(0, 0), K(0.45, 22 * D2R), K(1, 0)]),
      track("hips", [K(0, 0, 0, 0, 0, 0, 0), K(0.45, 0, 0, 0, 0, -0.04, 0.12), K(1, 0, 0, 0, 0, 0, 0)]),
    ],
  };
}

/** Spear thrust cycle: lunge forward with the spear, shield braced. */
function spearFightClip(name: string): AnimClip {
  return {
    name, duration: 0.9, loop: true, source: "procedural",
    tracks: [
      track("upperArmR", [K(0, -35 * D2R), K(0.4, -78 * D2R), K(0.55, -78 * D2R), K(1, -35 * D2R)]),
      track("lowerArmR", [K(0, -12 * D2R), K(0.4, -4 * D2R), K(1, -12 * D2R)]),
      track("chest", [K(0, 4 * D2R, -8 * D2R), K(0.4, 14 * D2R, 10 * D2R), K(1, 4 * D2R, -8 * D2R)]),
      track("upperArmL", [K(0, -45 * D2R, 0, 12 * D2R), K(1, -45 * D2R, 0, 12 * D2R)]),
      track("upperLegL", [K(0, 8 * D2R), K(0.4, 26 * D2R), K(1, 8 * D2R)]),
      track("hips", [K(0, 0, 0, 0, 0, 0, 0), K(0.4, 0, 0, 0, 0, -0.05, 0.16), K(1, 0, 0, 0, 0, 0, 0)]),
    ],
  };
}

/** Spear planted, body low behind the shield (defensive spearmen). */
function spearBlockClip(name: string): AnimClip {
  return {
    name, duration: 1.4, loop: true, source: "procedural",
    tracks: [
      track("hips", [K(0, 0, 0, 0, 0, -0.12, 0), K(0.5, 0, 0, 0, 0, -0.14, 0), K(1, 0, 0, 0, 0, -0.12, 0)]),
      track("upperLegL", [K(0, 28 * D2R), K(1, 28 * D2R)]),
      track("upperLegR", [K(0, 28 * D2R), K(1, 28 * D2R)]),
      track("upperArmR", [K(0, 18 * D2R), K(1, 18 * D2R)]),
      track("lowerArmR", [K(0, -8 * D2R), K(1, -8 * D2R)]),
      track("upperArmL", [K(0, -60 * D2R, 0, 14 * D2R), K(1, -60 * D2R, 0, 14 * D2R)]),
      track("chest", [K(0, 12 * D2R), K(0.5, 15 * D2R), K(1, 12 * D2R)]),
    ],
  };
}

/** Weapon-switch transition: hand to hip, draw, raise to guard. One-shot. */
function drawWeaponClip(name: string): AnimClip {
  return {
    name, duration: 0.7, loop: false, source: "procedural",
    tracks: [
      track("upperArmR", [K(0, 0), K(0.4, 42 * D2R), K(0.75, -34 * D2R), K(1, -30 * D2R)]),
      track("lowerArmR", [K(0, -20 * D2R), K(0.4, -95 * D2R), K(0.75, -30 * D2R), K(1, -28 * D2R)]),
      track("chest", [K(0, 0, 0, 10 * D2R), K(0.4, 6 * D2R, 0, 14 * D2R), K(1, 0)]),
      track("head", [K(0, 0), K(0.4, 12 * D2R), K(1, 0)]),
    ],
  };
}

/** Short chopping cuts with the sidearm. */
function gladiusFightClip(name: string): AnimClip {
  return {
    name, duration: 0.8, loop: true, source: "procedural",
    tracks: [
      track("upperArmR", [K(0, -58 * D2R), K(0.35, -12 * D2R), K(0.5, -64 * D2R), K(1, -58 * D2R)]),
      track("lowerArmR", [K(0, -24 * D2R), K(0.35, -6 * D2R), K(1, -24 * D2R)]),
      track("chest", [K(0, 6 * D2R, -12 * D2R), K(0.35, 10 * D2R, 12 * D2R), K(1, 6 * D2R, -12 * D2R)]),
      track("upperArmL", [K(0, -48 * D2R, 0, 12 * D2R), K(1, -48 * D2R, 0, 12 * D2R)]),
      track("hips", [K(0, 0, 0, 0, 0, -0.03, 0), K(0.35, 0, 0, 0, 0, -0.05, 0.06), K(1, 0, 0, 0, 0, -0.03, 0)]),
    ],
  };
}

/** Shield raised across the body, slight crouch (active blocking). */
function shieldBlockClip(name: string): AnimClip {
  return {
    name, duration: 1.2, loop: true, source: "procedural",
    tracks: [
      track("upperArmL", [K(0, -66 * D2R, 0, -22 * D2R), K(0.5, -70 * D2R, 0, -24 * D2R), K(1, -66 * D2R, 0, -22 * D2R)]),
      track("lowerArmL", [K(0, -52 * D2R), K(1, -52 * D2R)]),
      track("chest", [K(0, 8 * D2R, 0, -10 * D2R), K(1, 8 * D2R, 0, -10 * D2R)]),
      track("hips", [K(0, 0, 0, 0, 0, -0.08, 0), K(1, 0, 0, 0, 0, -0.08, 0)]),
      track("upperLegL", [K(0, 18 * D2R), K(1, 18 * D2R)]),
      track("upperLegR", [K(0, 18 * D2R), K(1, 18 * D2R)]),
      track("upperArmR", [K(0, -20 * D2R), K(1, -20 * D2R)]),
      track("head", [K(0, -10 * D2R), K(1, -10 * D2R)]),
    ],
  };
}

/** All procedural placeholder clips, keyed by exact manifest clip name. */
export function buildPlaceholderClips(): Map<string, AnimClip> {
  const m = new Map<string, AnimClip>();
  const put = (c: AnimClip) => m.set(c.name, c);
  put(idleClip("AN_faber_idle"));
  put(walkClip("AN_faber_walk"));
  put(hammerClip("AN_faber_hammer_high", true));
  put(hammerClip("AN_faber_hammer_low", false));
  put(sawClip("AN_faber_saw"));
  put(digClip("AN_faber_dig"));
  put(repairClip("AN_faber_repair"));
  put(idleClip("AN_generic_idle", 0.7));
  put(walkClip("AN_generic_walk"));
  put(attackClip("AN_generic_attack"));
  // Military placeholders (functional English names; see MILITARY_CLIP_SWAP).
  put(marchClip("AN_infantry_march"));
  put(doubleMarchClip("AN_infantry_double_march"));
  put(runClip("AN_infantry_run"));
  put(defensiveStanceClip("AN_infantry_defensive_stance"));
  put(throwClip("AN_infantry_throw"));
  put(spearFightClip("AN_infantry_spear_fight"));
  put(spearBlockClip("AN_infantry_spear_block"));
  put(drawWeaponClip("AN_infantry_draw_weapon"));
  put(gladiusFightClip("AN_infantry_gladius_fight"));
  put(shieldBlockClip("AN_infantry_shield_block"));
  return m;
}

/* ------------------------------------------------------------------ */
/* Clip registry + GLB swap                                            */
/* ------------------------------------------------------------------ */

export class ClipRegistry {
  private clips = new Map<string, AnimClip>();

  constructor() {
    this.registerPlaceholders();
  }

  registerPlaceholders(): void {
    for (const [name, clip] of buildPlaceholderClips()) {
      if (!this.clips.has(name)) this.clips.set(name, clip);
    }
  }

  /**
   * REAL-CLIP SWAP: convert THREE.AnimationClips from a loaded GLB and
   * replace the same-named placeholder slots. UE-style clip names that do
   * not match a slot are aliased through `aliasFor` (may be omitted —
   * then unknown names are ignored). Returns the names that were replaced
   * (`gltfName->slot` for aliases).
   */
  registerGltfClips(
    clips: THREE.AnimationClip[],
    aliasFor?: (gltfName: string) => string | null,
    restQuats?: Record<string, THREE.Quaternion>,
  ): string[] {
    const replaced: string[] = [];
    for (const clip of clips) {
      if (CLIP_ORDER.includes(clip.name)) {
        const sampled = sampleGltfClip(clip, 30, restQuats);
        if (!sampled) continue;
        this.clips.set(clip.name, sampled);
        replaced.push(clip.name);
        continue;
      }
      const slot = aliasFor?.(clip.name) ?? null;
      if (slot && this.aliasGltfClip(clip, slot, restQuats)) {
        replaced.push(`${clip.name}->${slot}`);
      }
    }
    return replaced;
  }

  /**
   * Map a real GLB clip onto an existing atlas slot (alias). The clip is
   * sampled via sampleGltfClip and REPLACES the slot's contents; the slot
   * keeps its CLIP_ORDER name so live atlas indices stay stable. Returns
   * false when the slot is unknown or the clip has no usable tracks —
   * a real clip never replaces a procedural one with a frozen pose.
   */
  aliasGltfClip(
    gltfClip: THREE.AnimationClip,
    slotName: string,
    restQuats?: Record<string, THREE.Quaternion>,
  ): boolean {
    if (!CLIP_ORDER.includes(slotName)) return false;
    const sampled = sampleGltfClip(gltfClip, 30, restQuats);
    if (!sampled) return false;
    this.clips.set(slotName, { ...sampled, name: slotName });
    return true;
  }

  get(name: string): AnimClip | undefined {
    return this.clips.get(name);
  }

  has(name: string): boolean {
    return this.clips.has(name);
  }

  /** Clip names actually supplied by the manifest asset (real or not-yet). */
  static wantedNames(model: ResolvedModel): string[] {
    return model.animations.filter((a) => CLIP_ORDER.includes(a));
  }
}

/**
 * Convert a THREE.AnimationClip to our baked format by sampling a dummy
 * rig at a fixed frame rate. UE-export track names are remapped onto the
 * placeholder rig through GLTF_BONE_REMAP (BONE_NAMES pass through
 * unchanged); rotation-only sampling keeps the placeholder's proportions.
 *
 * GLB tracks store the FULL local rotation (rest pose + motion). When
 * `restQuats` is given (ModelStore.loadBoneRestQuats), the rest
 * orientation is removed first, so only the motion delta lands on the
 * placeholder rig — without this, UE bones whose rest orientation differs
 * from the placeholder (e.g. thighs at ~180°) would deform wrongly.
 *
 * Returns null when no track survives the remap — the caller keeps the
 * procedural placeholder instead of a frozen pose.
 */
export function sampleGltfClip(
  clip: THREE.AnimationClip,
  fps = 30,
  restQuats?: Record<string, THREE.Quaternion>,
): AnimClip | null {
  const remapped = remapGltfTracks(clip, restQuats);
  if (!remapped) return null;
  const bones = BONE_NAMES.map((name) => {
    const o = new THREE.Object3D();
    o.name = name;
    return o;
  });
  for (let i = 0; i < BONE_COUNT; i++) {
    const p = BONE_PARENTS[i];
    const off = BONE_OFFSETS[i];
    bones[i].position.set(off[0], off[1], off[2]);
    if (p >= 0) bones[p].add(bones[i]);
  }
  const mixer = new THREE.AnimationMixer(bones[0]);
  const action = mixer.clipAction(remapped);
  action.play();
  const frames = Math.max(2, Math.round(remapped.duration * fps));
  const tracks: AnimTrack[] = BONE_NAMES.map((_, i) => ({
    bone: i,
    keys: [],
  }));
  const eul = new THREE.Euler();
  for (let f = 0; f <= frames; f++) {
    const t = (f / frames) * remapped.duration;
    mixer.setTime(t);
    // setTime alone does not pump the graph; update(0) applies it.
    mixer.update(0);
    for (let i = 0; i < BONE_COUNT; i++) {
      // Local rotation: the mixer writes the retargeted GLB bone-space
      // rotation onto the placeholder bone; bakeAtlas composes it onto
      // BONE_OFFSETS, so the motion survives the hierarchy change.
      eul.setFromQuaternion(bones[i].quaternion, "XYZ");
      tracks[i].keys.push({ t, rx: eul.x, ry: eul.y, rz: eul.z });
    }
  }
  action.stop();
  mixer.uncacheClip(remapped);
  return {
    name: clip.name,
    duration: remapped.duration,
    loop: true,
    source: "gltf",
    tracks,
  };
}

/**
 * Remap a GLB clip's tracks onto the placeholder rig: UE bone names go
 * through GLTF_BONE_REMAP, BONE_NAMES pass through unchanged, everything
 * else (prop meshes, extra joints) is dropped. Rotation-only — position
 * and scale tracks are ignored. When `restQuats` is given, each track's
 * rest orientation is removed (quaternion delta), so the placeholder
 * receives pure motion. Null when nothing usable survives.
 */
function remapGltfTracks(
  clip: THREE.AnimationClip,
  restQuats?: Record<string, THREE.Quaternion>,
): THREE.AnimationClip | null {
  const out: THREE.KeyframeTrack[] = [];
  const seen = new Set<string>();
  const q = new THREE.Quaternion();
  for (const track of clip.tracks) {
    const m = /^([^.]+)\.(quaternion|position|scale)$/.exec(track.name);
    if (!m) continue;
    const node = m[1];
    const bone =
      GLTF_BONE_REMAP[node.toLowerCase()] ??
      ((BONE_NAMES as readonly string[]).includes(node) ? node : undefined);
    if (!bone || m[2] !== "quaternion" || seen.has(bone)) continue;
    seen.add(bone);
    let nt = track.clone() as THREE.QuaternionKeyframeTrack;
    // Remove the GLB rest orientation: the track holds rest+motion, the
    // placeholder rig's rest is identity, so it takes the delta.
    const rest = restQuats?.[node.toLowerCase()];
    if (rest) {
      const inv = rest.clone().invert();
      const values = nt.values;
      for (let i = 0; i < values.length; i += 4) {
        q.set(values[i], values[i + 1], values[i + 2], values[i + 3]);
        q.premultiply(inv);
        values[i] = q.x;
        values[i + 1] = q.y;
        values[i + 2] = q.z;
        values[i + 3] = q.w;
      }
    }
    nt.name = `${bone}.quaternion`;
    out.push(nt);
  }
  if (out.length === 0) return null;
  return new THREE.AnimationClip(clip.name, clip.duration, out);
}

/* ------------------------------------------------------------------ */
/* Baked bone atlas                                                    */
/* ------------------------------------------------------------------ */

export const FRAMES_PER_CLIP = 32;

export interface BakedAtlas {
  data: Float32Array;
  /** Texels: bones * 4 wide. */
  width: number;
  /** Texels: framesPerClip * clipCount tall. */
  height: number;
  bones: number;
  framesPerClip: number;
  /** Clip name per atlas slot, in row order. */
  clipOrder: string[];
}

const _e = new THREE.Euler();
const _q = new THREE.Quaternion();
const _m = new THREE.Matrix4();
const _off = new THREE.Vector3();
const _parent = new THREE.Matrix4();

function trackValueAt(track: AnimTrack, t: number): AnimKey {
  const keys = track.keys;
  if (keys.length === 0) return { t, rx: 0, ry: 0, rz: 0 };
  if (t <= keys[0].t) return keys[0];
  const last = keys[keys.length - 1];
  if (t >= last.t) return last;
  for (let i = 0; i < keys.length - 1; i++) {
    const a = keys[i];
    const b = keys[i + 1];
    if (t >= a.t && t <= b.t) {
      const f = (t - a.t) / Math.max(1e-6, b.t - a.t);
      return {
        t,
        rx: a.rx + (b.rx - a.rx) * f,
        ry: a.ry + (b.ry - a.ry) * f,
        rz: a.rz + (b.rz - a.rz) * f,
        px: (a.px ?? 0) + ((b.px ?? 0) - (a.px ?? 0)) * f,
        py: (a.py ?? 0) + ((b.py ?? 0) - (a.py ?? 0)) * f,
        pz: (a.pz ?? 0) + ((b.pz ?? 0) - (a.pz ?? 0)) * f,
      };
    }
  }
  return last;
}

/**
 * Bake clips into a deterministic float atlas. Layout: row
 * (clipSlot * framesPerClip + frame) holds one bone matrix per 4
 * consecutive texels (columns of a column-major mat4). Skin matrix =
 * world(bone, t) * inverseBind(bone).
 */
export function bakeAtlas(clips: AnimClip[]): BakedAtlas {
  const bones = BONE_COUNT;
  const fpc = FRAMES_PER_CLIP;
  const width = bones * 4;
  const height = fpc * clips.length;
  const data = new Float32Array(width * height * 4);

  // Inverse bind pose.
  const bindWorld: THREE.Matrix4[] = [];
  const invBind: THREE.Matrix4[] = [];
  for (let i = 0; i < bones; i++) {
    const off = BONE_OFFSETS[i];
    _off.set(off[0], off[1], off[2]);
    _q.identity();
    _m.compose(_off, _q, new THREE.Vector3(1, 1, 1));
    const p = BONE_PARENTS[i];
    if (p >= 0) {
      _parent.copy(bindWorld[p]);
      bindWorld[i] = _parent.multiply(_m).clone();
    } else {
      bindWorld[i] = _m.clone();
    }
    invBind[i] = bindWorld[i].clone().invert();
  }

  const trackByBone = new Map<number, AnimTrack>();
  const world: THREE.Matrix4[] = [];
  for (let i = 0; i < bones; i++) world[i] = new THREE.Matrix4();

  clips.forEach((clip, slot) => {
    trackByBone.clear();
    for (const tr of clip.tracks) trackByBone.set(tr.bone, tr);
    for (let f = 0; f < fpc; f++) {
      const t = (f / (fpc - 1)) * clip.duration;
      for (let i = 0; i < bones; i++) {
        const kv = trackValueAt(
          trackByBone.get(i) ?? { bone: i, keys: [] },
          t,
        );
        const off = BONE_OFFSETS[i];
        _off.set(
          off[0] + (kv.px ?? 0),
          off[1] + (kv.py ?? 0),
          off[2] + (kv.pz ?? 0),
        );
        _e.set(kv.rx, kv.ry, kv.rz, "XYZ");
        _q.setFromEuler(_e);
        _m.compose(_off, _q, new THREE.Vector3(1, 1, 1));
        const p = BONE_PARENTS[i];
        if (p >= 0) world[i].multiplyMatrices(world[p], _m);
        else world[i].copy(_m);
        // skin = world * invBind
        _parent.multiplyMatrices(world[i], invBind[i]);
        const row = slot * fpc + f;
        const base = (row * width + i * 4) * 4;
        const el = _parent.elements;
        for (let c = 0; c < 16; c++) data[base + c] = el[c];
      }
    }
  });

  return {
    data,
    width,
    height,
    bones,
    framesPerClip: fpc,
    clipOrder: clips.map((c) => c.name),
  };
}

export function atlasTexture(baked: BakedAtlas): THREE.DataTexture {
  const tex = new THREE.DataTexture(
    baked.data,
    baked.width,
    baked.height,
    THREE.RGBAFormat,
    THREE.FloatType,
  );
  tex.needsUpdate = true;
  tex.minFilter = THREE.NearestFilter;
  tex.magFilter = THREE.NearestFilter;
  tex.generateMipmaps = false;
  return tex;
}

/* ------------------------------------------------------------------ */
/* Animation state machine (pure — headless testable)                  */
/* ------------------------------------------------------------------ */

export interface AnimPose {
  /** Clip slot indices into the atlas. */
  a: number;
  b: number;
  /** 0 = fully A, 1 = fully B. */
  blend: number;
  /** Clip-local time in seconds. */
  timeA: number;
  timeB: number;
  /** Timed events fired by the NEW clip this update (e.g. "release"). */
  events: string[];
  /** True when the new clip is non-looping and has reached its end. */
  finished: boolean;
}

export interface AnimSystemLike {
  clipIndexFor(activity: AnimActivity, faberSet: boolean): number;
  durationOf(slot: number): number;
  clipMeta(slot: number): { loop: boolean; events: Array<{ at: number; name: string }> };
}

/**
 * Formation/worker-level animation state: one per formation, one per
 * worker. Transitions blend over `blendTime` seconds. Times advance by dt
 * and wrap per the clip's own duration (clips have different lengths, so
 * wrap uses each clip's duration, not a global one).
 */
export class AnimController {
  private cur: AnimActivity = "idle";
  private prev: AnimActivity = "idle";
  private blendT = 1;
  private timeCur = 0;
  private timePrev = 0;
  private finishedCur = false;

  constructor(
    private sys: AnimSystemLike,
    private faberSet: boolean,
    private blendTime = 0.18,
  ) {}

  update(dt: number, activity: AnimActivity): AnimPose {
    if (activity !== this.cur) {
      this.prev = this.cur;
      this.timePrev = this.timeCur;
      this.cur = activity;
      this.timeCur = 0;
      this.blendT = 0;
      this.finishedCur = false;
    } else {
      this.blendT = Math.min(1, this.blendT + dt / this.blendTime);
    }
    const slotA = this.sys.clipIndexFor(this.cur, this.faberSet);
    const slotB = this.sys.clipIndexFor(this.prev, this.faberSet);
    const durA = this.sys.durationOf(slotA);
    const durB = this.sys.durationOf(slotB);
    const meta = this.sys.clipMeta(slotA);
    // Advance the new clip; non-looping clips clamp at the end (one-shots
    // like throw / draw_weapon) and report finished.
    const lastT = this.timeCur;
    let t = this.timeCur + dt;
    let wrapped = false;
    if (meta.loop) {
      const dur = Math.max(1e-6, durA);
      if (t >= dur) wrapped = true;
      t = t % dur;
    } else if (t >= durA) {
      t = durA;
      this.finishedCur = true;
    }
    this.timeCur = t;
    this.timePrev = (this.timePrev + dt) % Math.max(1e-6, durB);
    // Timed events: fire once when the playhead crosses each fraction,
    // including across a loop wrap (checked against both segments).
    const events: string[] = [];
    for (const e of meta.events) {
      const at = e.at * durA;
      const crossed = wrapped ? at > lastT || at <= t : at > lastT && at <= t;
      if (crossed) events.push(e.name);
    }
    const blend = this.blendT >= 1 || slotA === slotB ? 0 : 1 - this.blendT;
    return {
      // Pose A is the NEW clip; B is the outgoing one. Shader mixes
      // A->B by `blend`, so at blend=0 the pose is fully the new clip.
      a: slotA,
      b: slotB,
      blend,
      timeA: this.timeCur,
      timeB: this.timePrev,
      events,
      finished: this.finishedCur,
    };
  }

  /** Current activity (test hook). */
  activity(): AnimActivity {
    return this.cur;
  }
}

/* ------------------------------------------------------------------ */
/* Sim-state -> activity mappers (pure)                                */
/* ------------------------------------------------------------------ */

export interface WorkerAnimInfo {
  state: string;
  nodeKind: string | null;
  hasFarm: boolean;
}

/**
 * Faber behavior vocabulary: what the worker IS doing in the sim decides
 * what it LOOKS like it is doing. `cycleT` (seconds) alternates the two
 * hammer strikes so construction reads as varied work, not a looped gif.
 */
export function workerActivity(
  info: WorkerAnimInfo,
  cycleT: number,
): AnimActivity {
  switch (info.state) {
    case "move":
    case "toNode":
    case "toDropoff":
      return "walk";
    case "harvesting":
      if (info.hasFarm) return "dig";
      if (info.nodeKind === "timber") return "saw";
      return "hammer_low";
    case "constructing":
      return Math.floor(cycleT * 1.1) % 2 === 0 ? "hammer_high" : "hammer_low";
    case "repairing":
      return "repair";
    default:
      return "idle";
  }
}

/**
 * March pace: selects the movement gait clip. Wired to real inputs —
 * normal orders march, AI assault waves double-march, routers run.
 */
export type MarchPace = "march" | "double_march" | "run";

/** Per-formation melee animation memory (pilum state, draw transition). */
export interface MeleeAnimMemory {
  pilumThrown: boolean;
  /** Seconds left on the one-shot throw clip. */
  throwT: number;
  /** Seconds left on the one-shot draw_weapon transition. */
  drawT: number;
}

export function makeMeleeAnimMemory(): MeleeAnimMemory {
  return { pilumThrown: false, throwT: 0, drawT: 0 };
}

const THROW_DURATION = 1.1; // matches AN_infantry_throw
const DRAW_DURATION = 0.7; // matches AN_infantry_draw_weapon

/** Data-driven equipment check against the unit def's equipment list. */
function hasEquipment(
  equipment: readonly string[] | undefined,
  ...names: string[]
): boolean {
  if (!equipment) return false;
  // Substring match: data ids carry variants ("hasta_velitaris" matches
  // the "hasta" family, "pilum" still matches exactly).
  return equipment.some((id) => names.some((n) => id.includes(n)));
}

const SHIELD_KIT = ["scutum_oval", "scutum_curved", "scutum", "long_shield"];

/**
 * Resolve the melee activity for one tick (pure — advances mem by dt).
 *
 * Weapon logic is data-driven from the unit's `equipment` list:
 * - pilum carriers throw ONCE per engagement, then draw their sidearm
 *   (draw_weapon transition) and fight on with it;
 * - hasta/spear carriers fight spear_fight (spear_block when defensive);
 * - everyone else fights gladius_fight (shield_block when defensive).
 * Unknown kits fall back to gladius_fight so the state machine has no
 * holes; unshielded defensive units brace with their weapon instead.
 */
export function resolveMeleeActivity(
  mem: MeleeAnimMemory,
  dt: number,
  equipment: readonly string[] | undefined,
  defensive: boolean,
): AnimActivity {
  const spear = hasEquipment(equipment, "hasta", "spear");
  const shielded = hasEquipment(equipment, ...SHIELD_KIT);
  if (mem.throwT > 0) {
    mem.throwT -= dt;
    if (mem.throwT <= 0) {
      mem.pilumThrown = true;
      mem.drawT = DRAW_DURATION;
    }
    return "throw";
  }
  if (mem.drawT > 0) {
    mem.drawT -= dt;
    return "draw_weapon";
  }
  if (defensive) {
    if (shielded) return "shield_block";
    return spear ? "spear_block" : "gladius_fight";
  }
  return spear ? "spear_fight" : "gladius_fight";
}

/**
 * Call on melee contact (rising edge): pilum-armed formations open with
 * the throw clip; everyone else goes straight to their weapon cycle.
 */
export function beginMeleeEngagement(
  mem: MeleeAnimMemory,
  equipment: readonly string[] | undefined,
): void {
  if (hasEquipment(equipment, "pilum") && !mem.pilumThrown) {
    mem.throwT = THROW_DURATION;
  }
}

/** Call when a formation receives a fresh move order: pila are re-armed. */
export function rearmPilum(mem: MeleeAnimMemory): void {
  mem.pilumThrown = false;
  mem.throwT = 0;
  mem.drawT = 0;
}

export interface FormationAnimInput {
  moving: boolean;
  inMelee: boolean;
  pace: MarchPace;
  /** Defensive formation (testudo/close_ranks/...) — shields-up behavior. */
  defensive: boolean;
  /** Resolved by resolveMeleeActivity() each tick. */
  melee: AnimActivity;
}

/**
 * Formation sim state -> activity. Priority: melee beats movement beats
 * idle; defensive formations hold shields-up when stationary and block
 * when fighting; gait follows the march pace.
 */
export function formationActivity(input: FormationAnimInput): AnimActivity {
  if (input.inMelee) return input.melee;
  if (input.moving) {
    // Defensive formations never double-time: they advance at march pace.
    if (input.defensive) return "march";
    return input.pace;
  }
  if (input.defensive) return "defensive_stance";
  return "idle";
}

/* ------------------------------------------------------------------ */
/* AnimSystem: registry + atlas + index resolution                     */
/* ------------------------------------------------------------------ */

export class AnimSystem implements AnimSystemLike {
  readonly registry = new ClipRegistry();
  texture: THREE.DataTexture;
  private baked: BakedAtlas;
  /** Slot index per clip name. */
  private slotOf = new Map<string, number>();
  private durations: number[] = [];

  constructor() {
    this.baked = this.rebuild();
    this.texture = atlasTexture(this.baked);
  }

  private rebuild(): BakedAtlas {
    const clips = CLIP_ORDER.map((name) => this.registry.get(name)!).filter(
      Boolean,
    );
    const baked = bakeAtlas(clips);
    this.slotOf.clear();
    this.durations = [];
    baked.clipOrder.forEach((name, i) => {
      this.slotOf.set(name, i);
      const clip = clips[i];
      this.durations[i] = clip.duration;
    });
    return baked;
  }

  /**
   * REAL-CLIP SWAP (runtime): load a unit GLB's animation clips through
   * the ModelStore and replace same-named placeholder slots. UE-style clip
   * names are aliased through `aliasFor` (see attackClipAlias). Rebuilds
   * the atlas texture in place (same object identity — materials keep
   * working). Resolves to the replaced clip names.
   */
  async loadRealClips(
    model: ResolvedModel,
    store: ModelStore,
    aliasFor?: (gltfName: string) => string | null,
  ): Promise<string[]> {
    const clips = await store.loadAnimationClips(model);
    if (clips.length === 0) return [];
    // Rest orientations: GLB tracks hold rest+motion; the placeholder
    // rig takes the motion delta (see sampleGltfClip).
    const restQuats = await store.loadBoneRestQuats(model);
    const replaced = this.registry.registerGltfClips(clips, aliasFor, restQuats);
    if (replaced.length === 0) return [];
    const baked = this.rebuild();
    this.baked = baked;
    // Swap the texture image in place so existing materials keep rendering.
    this.texture.image = {
      data: baked.data,
      width: baked.width,
      height: baked.height,
    };
    this.texture.needsUpdate = true;
    return replaced;
  }

  clipIndexFor(activity: AnimActivity, faberSet: boolean): number {
    const want = ACTIVITY_CLIP[activity][faberSet ? "faber" : "generic"];
    const slot = this.slotOf.get(want);
    if (slot !== undefined) return slot;
    // Fallback: generic idle always exists.
    return this.slotOf.get("AN_generic_idle") ?? 0;
  }

  durationOf(slot: number): number {
    return this.durations[slot] ?? 1;
  }

  clipMeta(slot: number): {
    loop: boolean;
    events: Array<{ at: number; name: string }>;
  } {
    const name = this.baked.clipOrder[slot];
    const clip = name ? this.registry.get(name) : undefined;
    return {
      loop: clip?.loop ?? true,
      events: clip?.events ?? [],
    };
  }

  /** True when the unit's manifest entry names real faber clips. */
  faberClipSet(model: ResolvedModel): boolean {
    return ClipRegistry.wantedNames(model).some((n) =>
      (FABER_CLIP_NAMES as readonly string[]).includes(n),
    );
  }

  /** Diagnostics: clip name -> source (procedural/gltf). */
  clipSources(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const name of CLIP_ORDER) {
      const c = this.registry.get(name);
      if (c) out[name] = c.source;
    }
    return out;
  }
}

/**
 * Alias rule for real UE export clips. The vendored kits carry a single
 * "Attack" clip each (e.g. A_principes_kit_Attack). It replaces the unit's
 * sustained melee-fight slot — spear/hasta kits fight with the spear,
 * everyone else with the sidearm (mirrors resolveMeleeActivity's weapon
 * choice). The faber's overhead strike replaces its hammer_high
 * construction slot: the closest kinetic match, and the faber's actual
 * export motion. Anything else returns null (exact-name swap only).
 */
export function attackClipAlias(
  gltfName: string,
  faberSet: boolean,
  unit: UnitDef,
): string | null {
  if (!/attack/i.test(gltfName)) return null;
  if (faberSet) return "AN_faber_hammer_high";
  const equipment = unit.extra["equipment"] as readonly string[] | undefined;
  const spear =
    equipment?.some((id) => id.includes("hasta") || id.includes("spear")) ??
    false;
  return spear ? "AN_infantry_spear_fight" : "AN_infantry_gladius_fight";
}

/**
 * REAL-CLIP SWEEP (boot): for every unit type whose GLB is vendored, load
 * its embedded clips; same-named slots replace the procedural placeholders
 * and UE-style attack clips are aliased to the unit's melee slot via
 * attackClipAlias (indices stay stable, live meshes need no rebuild).
 * Units whose export carries no clips resolve to [] and are skipped.
 *
 * Runs in the background at boot; never rejects. Missing/failed GLBs keep
 * their placeholders.
 */
export async function loadRealClipsForUnits(
  sys: AnimSystem,
  units: Iterable<UnitDef>,
  resolve: (id: string) => ResolvedModel,
  store: ModelStore,
): Promise<string[]> {
  const replaced: string[] = [];
  for (const u of units) {
    let model: ResolvedModel;
    try {
      model = resolve(u.id);
    } catch {
      continue;
    }
    if (!model.available) continue;
    const faberSet = sys.faberClipSet(model);
    const aliasFor = (gltfName: string): string | null =>
      attackClipAlias(gltfName, faberSet, u);
    try {
      const names = await sys.loadRealClips(model, store, aliasFor);
      for (const n of names) replaced.push(`${u.id}:${n}`);
    } catch {
      // Keep the procedural placeholders for this unit.
    }
  }
  return replaced;
}
